import React, { useState, useRef, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { SingleFileUpload, MultipleFileUpload } from './FileUpload';
import { ImageIcon, DocumentTextIcon, FileIcon, XIcon } from './Icons';
import { normalizeFileName } from './utils/fileNameEncoding';

const EntityFileUpload = React.forwardRef(({
  className,
  multiple = false,
  accept,
  maxSize = 10 * 1024 * 1024,
  value,
  onChange,
  name,
  disabled = false,
  readOnly = false,
  placeholder = "Drop files here or click to browse",
  showPreview = true,
  maxFiles = 5,
  onError,
  
  // Entity-specific props
  entityTypeId,
  entityId,
  userId,
  description = '',
  isPrivate = false,
  
  // Callbacks
  onFileUpload,
  onFileRemove,
  
  ...props
}, ref) => {
  const { theme } = useTheme();
  const [uploading, setUploading] = useState(false);

  const isDisabled = disabled || readOnly;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleFiles = useCallback(async (files) => {
    if (!entityTypeId || !entityId) {
      onError?.('Entity type and entity ID are required for file upload');
      return;
    }

    setUploading(true);
    
    try {
      const fileArray = Array.from(files);
      const fileAttachments = [];

      for (const file of fileArray) {
        // Create file attachment object
        const fileAttachment = {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          description: description,
          isPrivate: isPrivate,
          entityTypeId: Number(entityTypeId),
          entityId: Number(entityId),
          userId: userId ? Number(userId) : null,
          uploadedAt: new Date().toISOString(),
          isDeleted: false
        };

        // If it's a text file, read its contents
        if (file.type.startsWith('text/') || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
          try {
            const textContent = await readFileAsText(file);
            fileAttachment.textContents = textContent;
          } catch (error) {
            console.warn('Could not read text content from file:', file.name);
          }
        }

        fileAttachments.push(fileAttachment);
      }

      // Call the upload callback
      if (onFileUpload) {
        await onFileUpload(fileAttachments, files);
      }

      // Update the form value
      if (onChange) {
        const event = {
          target: {
            name,
            value: multiple ? fileAttachments : fileAttachments[0],
            type: 'file',
            files: fileArray,
            fileAttachments: fileAttachments
          }
        };
        onChange(event);
      }

    } catch (error) {
      onError?.(error.message || 'Error processing files');
    } finally {
      setUploading(false);
    }
  }, [entityTypeId, entityId, userId, description, isPrivate, multiple, onChange, onFileUpload, onError, name]);

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  const handleFileRemove = useCallback((index) => {
    if (onFileRemove) {
      const currentFiles = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);
      const fileToRemove = currentFiles[index];
      onFileRemove(fileToRemove, index);
    }
  }, [multiple, value, onFileRemove]);

  const renderFilePreview = (fileAttachment, index) => {
    if (!showPreview) return null;

    const normalizedFileName = normalizeFileName(fileAttachment.fileName || '');
    const isImage = fileAttachment.fileType?.startsWith('image/');
    const isText = fileAttachment.fileType?.startsWith('text/') || 
                   normalizedFileName.endsWith('.txt') || 
                   normalizedFileName.endsWith('.md');

    return (
      <div key={index} className="relative group flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
        {/* File Icon */}
        <div className="flex-shrink-0">
          {isImage ? (
            <ImageIcon className="w-6 h-6 text-blue-500" />
          ) : isText ? (
            <DocumentTextIcon className="w-6 h-6 text-green-500" />
          ) : (
            <FileIcon className="w-6 h-6 text-gray-500" />
          )}
        </div>

        {/* File Info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {normalizedFileName}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>{formatFileSize(fileAttachment.fileSize)}</span>
            {fileAttachment.isPrivate && (
              <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded text-xs">
                Private
              </span>
            )}
            {fileAttachment.textContents && (
              <span className="px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-xs">
                Text
              </span>
            )}
          </div>
          {fileAttachment.description && (
            <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 truncate">
              {fileAttachment.description}
            </p>
          )}
        </div>

        {/* Remove Button */}
        <button
          type="button"
          onClick={() => handleFileRemove(index)}
          className="w-6 h-6 bg-red-500 text-white rounded-full 
                   flex items-center justify-center opacity-0 group-hover:opacity-100 
                   transition-opacity duration-200 hover:bg-red-600"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const files = multiple ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  return (
    <div className={cn("space-y-4", className)}>
      {/* Upload Area */}
      <div
        className={cn(
          "relative w-full min-h-[120px] rounded-md border-2 border-dashed transition-all duration-200",
          uploading ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-gray-400",
          theme.colors.surface,
          !isDisabled && !uploading && 'cursor-pointer'
        )}
      >
        {multiple ? (
          <MultipleFileUpload
            accept={accept}
            maxSize={maxSize}
            maxFiles={maxFiles}
            placeholder={uploading ? "Uploading files..." : placeholder}
            disabled={isDisabled || uploading}
            onChange={handleFiles}
            onError={onError}
            {...props}
          />
        ) : (
          <SingleFileUpload
            accept={accept}
            maxSize={maxSize}
            placeholder={uploading ? "Uploading file..." : placeholder}
            disabled={isDisabled || uploading}
            onChange={handleFiles}
            onError={onError}
            {...props}
          />
        )}
      </div>

      {/* File Previews */}
      {files.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {multiple ? `Attached Files (${files.length})` : 'Attached File'}
          </h4>
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
            {files.map((fileAttachment, index) => renderFilePreview(fileAttachment, index))}
          </div>
        </div>
      )}

      {/* Entity Info */}
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {entityTypeId && entityId && (
          <p>Entity: Type {entityTypeId}, ID {entityId}</p>
        )}
        {!entityTypeId || !entityId ? (
          <p className="text-yellow-600">⚠️ Entity type and ID are required for file upload</p>
        ) : null}
      </div>
    </div>
  );
});

// Single Entity File Upload Component
const SingleEntityFileUpload = React.forwardRef((props, ref) => {
  return <EntityFileUpload ref={ref} multiple={false} {...props} />;
});

// Multiple Entity Files Upload Component
const MultipleEntityFileUpload = React.forwardRef((props, ref) => {
  return <EntityFileUpload ref={ref} multiple={true} {...props} />;
});

EntityFileUpload.displayName = "EntityFileUpload";
SingleEntityFileUpload.displayName = "SingleEntityFileUpload";
MultipleEntityFileUpload.displayName = "MultipleEntityFileUpload";

export { EntityFileUpload, SingleEntityFileUpload, MultipleEntityFileUpload };
